<template>
    <div class="content-wrapper">
        <section class="content-header">
            <h1>
                {{ batchContactsListId }} - {{ $t('batchContactsList.batchContactsList') }}
                <template v-if="isLoaded && batchContactsList.name"> - {{ batchContactsList.name }} </template>

                <div
                    v-if="isLoaded"
                    class="pull-right"
                >
                    <router-link
                        :to="{ name: 'lists.batchContacts.list' }"
                        class="btn btn-sm btn-default"
                    >
                        <span class="sr-only">{{ $t('batchContactsList.batchContactsLists') }}</span>
                        <span class="hidden-xs">{{ $t('batchContactsList.batchContactsLists') }}</span>
                        <span class="visible-xs">
                            <i class="fa fa-list-ul"></i>
                        </span>
                    </router-link>

                    <button
                        class="btn btn-sm btn-success"
                        data-toggle="modal"
                        data-target="#confirm-refresh-modal"
                        title="Refresh Batch Contacts"
                        :disabled="
                            batchContactsList.status === BATCH_CONTACTS_LIST_STATUS_FRESH ||
                            batchContactsList.status === BATCH_CONTACTS_LIST_STATUS_PROCESSING
                        "
                    >
                        <span class="sr-only">Refresh</span>
                        <span class="hidden-xs">Refresh</span>
                        <span class="visible-xs">
                            <i class="fa fa-refresh"></i>
                        </span>
                    </button>

                    <button
                        class="btn btn-sm btn-danger"
                        data-toggle="modal"
                        data-target="#confirm-refresh-modal"
                        title="Refresh Batch Failed Contacts"
                        :disabled="
                            batchContactsList.status === BATCH_CONTACTS_LIST_STATUS_FRESH ||
                            batchContactsList.status === BATCH_CONTACTS_LIST_STATUS_PROCESSING
                        "
                    >
                        <span class="sr-only">Refresh Failed</span>
                        <span class="hidden-xs">Refresh Failed</span>
                        <span class="visible-xs">
                            <i class="fa fa-refresh"></i>
                        </span>
                    </button>

                    <router-link
                        v-if="isLoaded && !is404"
                        :to="{
                            name: 'lists.batchContacts.edit',
                            params: { id: batchContactsListId },
                        }"
                        class="btn btn-sm btn-primary"
                    >
                        <span class="sr-only">{{ $t('edit') }}</span>
                        <span class="hidden-xs">{{ $t('edit') }}</span>
                        <span class="visible-xs">
                            <i class="fa fa-edit"></i>
                        </span>
                    </router-link>
                </div>
            </h1>
        </section>

        <section class="content">
            <Alerts />

            <div class="row">
                <div class="col-xs-12">
                    <div class="box box-primary">
                        <div
                            v-if="!isLoaded"
                            class="box-body with-border clearfix"
                        >
                            <img
                                class="table-row-preloader"
                                rel="preload"
                                src="/img/table-row-preloader.gif"
                            />
                        </div>

                        <template v-else>
                            <div
                                v-if="is404 || !batchContactsList.id"
                                class="box-body with-border clearfix"
                            >
                                <div class="row">
                                    <div class="col-xs-12">
                                        <p>
                                            {{ $t('messages.batchContactsList.noBatchContactsList', { id: batchContactsListId }) }}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <template v-else>
                                <div class="box-header with-border">
                                    <h3 class="box-title">{{ $t('info') }}</h3>
                                </div>

                                <div class="box-body">
                                    <div class="row">
                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong>{{ $t('name') }}:</strong>
                                            <p>{{ batchContactsList.name }}</p>
                                        </div>

                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong>{{ $t('active') }}:</strong>
                                            <p>
                                                <ValueYesNo :value="Boolean(batchContactsList.active)" />
                                            </p>
                                        </div>

                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong>{{ $t('createdAt') }}:</strong>
                                            <p>
                                                {{ formatDate(batchContactsList.createdAt) }}
                                            </p>
                                        </div>

                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong>{{ $t('updatedAt') }}:</strong>
                                            <p>
                                                {{ formatDate(batchContactsList.updatedAt) }}
                                            </p>
                                        </div>
                                    </div>

                                    <div class="row">
                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong>{{ $t('status') }}:</strong>
                                            <p>
                                                <span
                                                    class="label"
                                                    :class="{
                                                        'bg-primary': batchContactsList.status === BATCH_CONTACTS_LIST_STATUS_FRESH,
                                                        'bg-green': batchContactsList.status === BATCH_CONTACTS_LIST_STATUS_READY,
                                                        'bg-red': batchContactsList.status === BATCH_CONTACTS_LIST_STATUS_FAILED,
                                                        'bg-yellow': batchContactsList.status === BATCH_CONTACTS_LIST_STATUS_PROCESSING,
                                                        'bg-orange': batchContactsList.status === BATCH_CONTACTS_LIST_STATUS_REFRESHING,
                                                    }"
                                                    :title="$t(batchContactsList.status.toLowerCase())"
                                                >
                                                    {{ $t(batchContactsList.status.toLowerCase()) }}
                                                </span>
                                            </p>
                                        </div>

                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong>{{ $t('batchContactsList.totalContacts') }}:</strong>
                                            <p>
                                                <BadgeTotal
                                                    :value="batchContactsList.all"
                                                    color="gray"
                                                />
                                            </p>
                                        </div>

                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong>{{ $t('batchContactsList.readyContacts') }}:</strong>
                                            <p>
                                                <BadgeTotal
                                                    :value="batchContactsList.ready"
                                                    color="green"
                                                />
                                            </p>
                                        </div>

                                        <div class="col-xs-12 col-sm-6 col-md-3">
                                            <strong>{{ $t('batchContactsList.labels') }}:</strong>
                                            <ListItems :rows="batchContactsList.labels" />
                                        </div>

                                    </div>
                                    <div class="row">
                                        <div
                                            v-if="batchContactsList.fileName"
                                            class="col-xs-12 col-sm-6 col-md-3"
                                        >
                                            <strong>{{ $t('batchContactsList.uploadedFile') }}:</strong>
                                            <div>
                                                (<a
                                                :href="`/lists/batch-contacts/download/upload-file/${batchContactsList.id}`"
                                                >
                                                {{ batchContactsList.fileName }}
                                                </a>)
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </template>
                    </div>
                </div>
            </div>
            <ListBatchContactsStats
                :stats="stats.carriers"
                :isLoaded="isLoaded"
                title="Carrier Stats"
            />
            <ListBatchContactsStats
                :stats="stats.timezones"
                :isLoaded="isLoaded"
                title="Phone timezones Stats"
            />
        </section>

        <ModalDialog
            id="confirm-refresh-modal"
            ref="modal"
            :class="{
                'modal-success': !isRefreshFailed,
                'modal-danger': isRefreshFailed,
            }"
            size="sm"
            :isLoading="isLoading"
            @submit="refreshBatchContactsList"
        >
            <template #header>{{ isRefreshFailed ? 'Refresh Failed Contacts?' : 'Refresh Contacts?' }}</template>

            <template #footer="{ close, submit }">
                <button
                    class="btn btn-sm btn-default"
                    type="button"
                    data-dismiss="modal"
                    :disabled="isLoading"
                    @click="close"
                >
                    {{ $t('cancel') }}
                </button>

                <button
                    class="btn btn-sm"
                    :class="{
                        'btn-success': !isRefreshFailed,
                        'btn-danger': isRefreshFailed,
                    }"
                    :disabled="isLoading ||
                        batchContactsList.status === BATCH_CONTACTS_LIST_STATUS_FRESH ||
                        batchContactsList.status === BATCH_CONTACTS_LIST_STATUS_PROCESSING ||
                        batchContactsList.status === BATCH_CONTACTS_LIST_STATUS_REFRESHING
                    "
                    @click="submit"
                >
                    <i
                        v-if="isLoading"
                        class="fa fa-refresh fa-spin"
                    />
                    <span>Refresh</span>
                </button>
            </template>
        </ModalDialog>
    </div>
</template>

<script>
import EventBus from '@/event-bus';
import formatsMixin from '@/mixins/formatsMixin';
import batchContactsListMixin from '@/mixins/batchContactsListMixin';
import BadgeTotal from '@/components/subcomponents/BadgeTotal';
import ModalDialog from '@/components/modals/ModalDialog';
import ValueYesNo from '@/components/subcomponents/ValueYesNo';
import ListBatchContactsStats from "@/components/lists/batch-contacts/ListBatchContactsStats.vue";
import ListItems from "../../../components/subcomponents/ListItems.vue";

export default {
    components: {
        ListItems,
        BadgeTotal,
        ValueYesNo,
        ModalDialog,
        ListBatchContactsStats,
    },
    mixins: [formatsMixin, batchContactsListMixin],
    data() {
        return {
            is404: false,
            isLoading: false,
            isLoaded: false,
            timeout: 0,
            batchContactsListId: this.$route.params.id,
            batchContactsList: {},
            isRefreshFailed: false,
            stats: [],
            isCollapse: true,
        };
    },
    computed: {
        headTitle() {
            return this.batchContactsList.name && `${this.$t('batchContactsList.batchContactsList')} ${this.batchContactsList.name}`;
        },
    },

    created() {
        this.fetchBatchContactsList();
    },

    methods: {
        async fetchBatchContactsList() {
            try {
                const {
                    data: { batchContactsList = {}, stats = [] },
                } = await axios.get(`/lists/batch-contacts/data/${this.batchContactsListId}`);

                this.batchContactsList = batchContactsList;
                this.stats = stats;

                this.checkProcessing();
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoaded = true;
            }
        },

        async refreshBatchContactsList() {
            this.batchContactsList.status = this.BATCH_CONTACTS_LIST_STATUS_REFRESHING;
            this.$refs.modal.close();

            try {
                if (this.isRefreshFailed) {
                    await axios.put(`/lists/batch-contacts/refresh-failed/${this.batchContactsList.id}`);
                } else {
                    await axios.put(`/lists/batch-contacts/refresh/${this.batchContactsList.id}`);
                }
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            }
        },

        checkProcessing() {
            const isProcessing = this.batchContactsList.status === this.BATCH_CONTACTS_LIST_STATUS_PROCESSING;

            clearTimeout(this.timeout);

            if (isProcessing) {
                this.timeout = setTimeout(() => {
                    this.fetchBatchContactsList();
                }, 2000);
            }
        },
    },
};
</script>
